import { isRichTextValueConsideredEmpty } from "@uniformdev/richtext";

import { ContentBlock } from "@/libraries/design/components";
import { ButtonVariant } from "@/libraries/design/components/button/src/button";
import {
    ContentBlockAlignment,
    ContentBlockTitleAs,
    ContentBlockTitleAppearsAs,
    ContentBlockCtaProps,
    ContentBlockColumns,
    ContentBlockLogoLockups,
    ContentBlockImageAspectRatio
} from "@/libraries/design/components/content-block/src/content-block";
import { LinkType } from "@/libraries/design/components/link/src/link";
import {
    RichTextComponent,
    type SectionDividerType
} from "@/libraries/design/utils";
import { useLocalizedHref } from "@/libraries/web-framework";

export type UniformContentBlockProps = {
    title?: string;
    text?: string;
    uniformRichText?: unknown;
    richText?: string; // for rich text from Contentful
    logo?: keyof typeof ContentBlockLogoLockups;
    eyebrow?: string;
    titleAs?: ContentBlockTitleAs;
    titleAppearsAs?: ContentBlockTitleAppearsAs;
    buttonHref?: {
        path: string;
    };
    buttonLabel?: string;
    buttonVariant?: ButtonVariant;
    linkHref?: {
        path: string;
    };
    linkText?: string;
    linkType?: LinkType;
    alignment?: ContentBlockAlignment;
    imageUrl?: string;
    imageAlt?: string;
    imageAspectRatio?: ContentBlockImageAspectRatio;
    spotIllustrationUrl?: string;
    iconUrl?: string;
    iconSize?: "x-small" | "small" | "medium" | "large";
    iconAlt?: string;
    orientation?: "vertical" | "horizontal" | "horizontal-reverse";
    columns?: "10" | "12";
    backgroundColor?: string;
    hat?: SectionDividerType;
    pants?: SectionDividerType;
};

const UniformContentBlock = (props: UniformContentBlockProps) => {
    const localizedLinkHref = useLocalizedHref(props.linkHref?.path ?? "");
    const localizedButtonHref = useLocalizedHref(props.buttonHref?.path ?? "");

    const callToActionProps: ContentBlockCtaProps | undefined =
        transformCtaProps({
            ...props,
            linkHref: props.linkHref ? { path: localizedLinkHref } : undefined,
            buttonHref: props.buttonHref
                ? { path: localizedButtonHref }
                : undefined
        });

    return (
        <ContentBlock
            title={props.title}
            text={
                props.richText ? (
                    <RichTextComponent text={props.richText} />
                ) : !isRichTextValueConsideredEmpty(props.uniformRichText) ? (
                    <RichTextComponent uniformParameter="uniformRichText" />
                ) : (
                    props.text
                )
            }
            titleAs={props.titleAs}
            titleAppearsAs={props.titleAppearsAs}
            logo={props.logo}
            eyebrow={props.eyebrow}
            alignment={props.alignment}
            callToActionProps={callToActionProps}
            imageProps={
                props.imageUrl
                    ? {
                          src: props.imageUrl,
                          alt: props.imageAlt ?? ""
                      }
                    : undefined
            }
            imageAspectRatio={props.imageAspectRatio}
            spotIllustrationUrl={props.spotIllustrationUrl}
            icon={
                props.iconUrl
                    ? {
                          linkedIcon: {
                              url: props.iconUrl,
                              size: props.iconSize,
                              alt: props.iconAlt ?? ""
                          }
                      }
                    : undefined
            }
            orientation={props.orientation}
            columns={props.columns as unknown as ContentBlockColumns}
            backgroundColor={props.backgroundColor}
            hat={props.hat}
            pants={props.pants}
        />
    );
};

function transformCtaProps({
    buttonHref,
    buttonLabel,
    buttonVariant,
    linkHref,
    linkText,
    linkType
}: UniformContentBlockProps): ContentBlockCtaProps | undefined {
    const isButton = !!buttonHref?.path && !!buttonLabel;
    const isLink = !!linkHref?.path && !!linkText;

    if (isButton || isLink) {
        const ctaProps = {
            button: isButton
                ? {
                      href: buttonHref.path,
                      dataEventProps: {
                          "data-product-key": "uniform",
                          "data-event-component": "linkButton",
                          "data-event-container": "contentBlock",
                          "data-event": "clicked",
                          "data-label": buttonLabel
                      }
                  }
                : undefined,
            buttonText: buttonLabel ?? undefined,
            buttonVariant: buttonVariant ?? undefined,
            link: isLink
                ? {
                      href: linkHref.path,
                      text: linkText,
                      type: linkType,
                      dataEventProps: {
                          "data-product-key": "uniform",
                          "data-event-component": "linkButton",
                          "data-event-container": "contentBlock",
                          "data-event": "clicked",
                          "data-label": linkText
                      }
                  }
                : undefined
        } as ContentBlockCtaProps;

        return ctaProps;
    }

    return undefined;
}

export default UniformContentBlock;
